import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { LoginCredentials } from '@/types';
import Button from '@/components/ui/Button';
import { Dumbbell, Eye, EyeOff, Sun, Moon } from 'lucide-react';
import { useServerWake, waitForServer } from '@/hooks/useServerWake';

const loginSchema = z.object({
  identifier: z.string().min(1, 'Email or username is required'),
  password: z.string().min(1, 'Password is required'),
});

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('Signing in...');
  // Quietly wakes the backend on mount; no notice is shown to the user.
  useServerWake();
  const [fieldError, setFieldError] = useState<{ field: 'identifier' | 'password' | 'general'; message: string } | null>(null);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // ProtectedRoute stashes the URL the user was denied here, so signing in
  // resumes where they were headed instead of always dropping them on the
  // dashboard.
  const redirectTo = (location.state as any)?.from?.pathname || '/dashboard';

  const { register, handleSubmit, formState: { errors } } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
  });


  const onSubmit = async (data: LoginCredentials) => {
    setIsLoading(true);
    setFieldError(null);
    setLoadingMsg('Signing in...');
    const hint = setTimeout(() => setLoadingMsg('Just a moment…'), 5000);
    try {
      // On a cold start this holds the request until the server is up, so it
      // doesn't time out. Already warm: resolves immediately.
      await waitForServer();
      await login(data);
      clearTimeout(hint);
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      clearTimeout(hint);

      const status    = error?.response?.status;
      const apiMsg    = error?.response?.data?.message as string | undefined;
      const apiCode   = error?.response?.data?.code as string | undefined;
      const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
      const isNetwork = error?.message === 'Network Error' || !error?.response;

      if (isTimeout || (isNetwork && !status)) {
        setFieldError({ field: 'general', message: "We couldn't reach FitTrack. Check your connection and try again." });
      } else if (status === 404) {
        setFieldError({ field: 'general', message: "We couldn't reach FitTrack. Check your connection and try again." });
      } else if (status === 401) {
        if (apiCode === 'USER_NOT_FOUND') {
          setFieldError({ field: 'identifier', message: "The account you entered doesn't exist. Check your details or create a new account." });
        } else if (apiCode === 'WRONG_PASSWORD') {
          setFieldError({ field: 'password', message: "The password you've entered is incorrect." });
        } else {
          setFieldError({ field: 'general', message: apiMsg || 'Incorrect email/username or password.' });
        }
      } else if (status === 429) {
        setFieldError({ field: 'general', message: 'Too many attempts — please wait a minute and try again.' });
      } else if (status && status >= 500) {
        setFieldError({ field: 'general', message: 'Server error — please try again in a moment.' });
      } else {
        setFieldError({ field: 'general', message: apiMsg || 'Something went wrong. Please try again.' });
      }
    } finally {
      setIsLoading(false);
      setLoadingMsg('Signing in...');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-app py-12 px-4">
      {/* Theme toggle — top right */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 p-2 rounded-xl bg-subtle border border-line text-ink-muted hover:text-ink transition-all z-10"
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-2xl bg-accent/10 border border-accent/20">
              <Dumbbell className="h-10 w-10 text-accent" />
            </div>
          </div>
          <h1 className="text-3xl font-black text-ink">Welcome back</h1>
          <p className="mt-2 text-sm text-ink-muted">Sign in to continue your fitness journey</p>
        </div>

        {/* Card */}
        <div className="bg-surface border border-line rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.5)]">

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* General error banner */}
            {fieldError?.field === 'general' && (
              <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-sm text-red-400">{fieldError.message}</p>
              </div>
            )}

            {/* Email/Username */}
            <div>
              <label htmlFor="login-identifier" className="block text-sm font-medium text-ink-muted mb-1.5">
                Email or Username
              </label>
              <input
                id="login-identifier"
                {...register('identifier')}
                type="text"
                autoComplete="username"
                placeholder="Enter your email or username"
                className={`block w-full px-4 py-3 bg-subtle border rounded-xl
                           text-ink placeholder-ink-faint text-sm
                           focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent/50
                           transition-colors ${
                             fieldError?.field === 'identifier'
                               ? 'border-red-500/60 bg-red-500/5'
                               : 'border-line'
                           }`}
              />
              {errors.identifier && (
                <p className="mt-1.5 text-xs text-red-400">{errors.identifier.message}</p>
              )}
              {fieldError?.field === 'identifier' && (
                <div className="mt-2 flex items-start gap-2">
                  <p className="text-sm text-red-400">{fieldError.message}</p>
                </div>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-ink-muted mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  className={`block w-full px-4 py-3 pr-11 bg-subtle border rounded-xl
                             text-ink placeholder-ink-faint text-sm
                             focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent/50
                             transition-colors ${
                               fieldError?.field === 'password'
                                 ? 'border-red-500/60 bg-red-500/5'
                                 : 'border-line'
                             }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-muted hover:text-ink"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-400">{errors.password.message}</p>
              )}
              {fieldError?.field === 'password' && (
                <div className="mt-2 flex items-start justify-between gap-2">
                  <p className="text-sm text-red-400">{fieldError.message}</p>
                  <Link to="/forgot-password" className="text-xs text-accent hover:brightness-110 whitespace-nowrap flex-shrink-0">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            <Button type="submit" loading={isLoading} className="w-full" size="lg">
              {isLoading ? loadingMsg : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <Link to="/forgot-password" className="block text-sm text-accent hover:brightness-110 transition-colors">
              Forgot your password?
            </Link>
            <p className="text-sm text-ink-dim">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-accent hover:brightness-110 transition-colors">
                Sign up here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
